import React, { useState } from 'react';
import { FileText } from 'lucide-react';

function CertificateList({ certificates, onEdit, onDelete, onDownload, onPreview, selectedIds, onToggleSelection, onSelectAll, onEmail }) {
  const [searchTerm, setSearchTerm] = useState('');

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'No expiry';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Calculate days until expiry
  const getDaysUntilExpiry = (expiryDate) => {
    if (!expiryDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(expiryDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Get status
  const getStatus = (expiryDate) => {
    if (!expiryDate) return 'valid';
    const days = getDaysUntilExpiry(expiryDate);
    if (days < 0) return 'expired';
    if (days <= 90) return 'soon';
    return 'valid';
  };

  // Get status text
  const getStatusText = (expiryDate) => {
    if (!expiryDate) return 'Valid';
    const days = getDaysUntilExpiry(expiryDate);
    if (days < 0) return 'Expired';
    if (days <= 90) return 'Expiring soon';
    return 'Valid';
  };

  // Filter certificates
  const filteredCertificates = certificates.filter((cert) => {
    const search = searchTerm.toLowerCase();
    
    // Search in name (use certificate_name if available)
    const certName = (cert.certificate_name || cert.name || '').toLowerCase();
    if (certName.includes(search)) return true;
    
    // Search in type
    if (cert.type.toLowerCase().includes(search)) return true;
    
    // Search in units
    if (cert.units && Array.isArray(cert.units)) {
      return cert.units.some(
        (unit) =>
          unit.code.toLowerCase().includes(search) ||
          unit.name.toLowerCase().includes(search)
      );
    }
    
    return false;
  });

  // Sort by expiry date (certificates without expiry go to end)
  const sortedCertificates = [...filteredCertificates].sort((a, b) => {
    if (!a.expiry_date) return 1;
    if (!b.expiry_date) return -1;
    return new Date(a.expiry_date) - new Date(b.expiry_date);
  });

  // Handle delete with confirmation
  const handleDelete = (cert) => {
    if (window.confirm(`Are you sure you want to delete "${cert.name}"?`)) {
      onDelete(cert.id);
    }
  };

  return (
    <>
      {selectedIds.size > 0 && (
        <div className="bulk-bar">
          <div className="toolbar-left">
            <span className="selection-count">
              {selectedIds.size} selected
            </span>
            <button className="btn btn-outline" onClick={onSelectAll}>
              Clear
            </button>
          </div>
          <span className="selection-info">
            {certificates.filter((c) => selectedIds.has(c.id) && c.file_url).length} with files
          </span>
        </div>
      )}

      <div className="table-card card">
        <div className="table-head">
          <div className="table-head-left">
            <h2 className="table-title">Certificates & Qualifications</h2>
          </div>
          <div className="table-head-right">
            <button 
              className={`btn ${selectedIds.size > 0 ? 'btn-primary' : 'btn-disabled'}`}
              onClick={onEmail}
              disabled={selectedIds.size === 0}
              title={selectedIds.size === 0 ? 'Select certificates to email' : `Email ${selectedIds.size} certificate${selectedIds.size > 1 ? 's' : ''}`}
            >
              📧 Email
            </button>
            <input
              type="search"
              className="search-box"
              placeholder="Search certificates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <table className="table">
          <thead>
            <tr>
              <th style={{ width: '50px' }}>
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={selectedIds.size === certificates.length && certificates.length > 0}
                  onChange={onSelectAll}
                />
              </th>
              <th>Certificate Name</th>
              <th>Issue Date</th>
              <th>Expiry Date</th>
              <th>Status</th>
              <th>File</th>
              <th className="col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedCertificates.length === 0 ? (
              <tr>
                <td colSpan="7" className="empty-state">
                  <div className="empty-state-icon">📋</div>
                  {searchTerm ? (
                    <>
                      <div className="empty-state-title">No certificates found</div>
                      <div className="empty-state-text">
                        Try adjusting your search terms
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="empty-state-title">No certificates yet</div>
                      <div className="empty-state-text">
                        Add your first certificate to start tracking expiry dates and stay compliant.
                      </div>
                    </>
                  )}
                </td>
              </tr>
            ) : (
              sortedCertificates.map((cert) => {
                const status = getStatus(cert.expiry_date);
                return (
                  <tr key={cert.id}>
                    <td>
                      <input
                        type="checkbox"
                        className="checkbox"
                        checked={selectedIds.has(cert.id)}
                        onChange={() => onToggleSelection(cert.id)}
                      />
                    </td>
                    <td>
                      <div className="cert-name">
                        {cert.certificate_name || cert.name}
                      </div>
                      <span className="cert-type">{cert.type}</span>
                      {cert.units && cert.units.length > 0 && (
                        <div className="cert-units-badge">
                          {cert.units.length} unit{cert.units.length > 1 ? 's' : ''}
                        </div>
                      )}
                    </td>
                    <td>{formatDate(cert.issue_date)}</td>
                    <td>{formatDate(cert.expiry_date)}</td>
                    <td>
                      <span className={`badge badge-${status}`}>
                        {getStatusText(cert.expiry_date)}
                      </span>
                    </td>
                    <td>
                      {cert.file_url ? (
                        <button
                          className="file-link"
                          onClick={() => onPreview(cert)}
                          title={cert.file_name || 'Click to preview'}
                        >
                          <FileText size={16} />
                          <span className="file-link-name">{cert.file_name || 'File attached'}</span>
                        </button>
                      ) : (
                        <span className="file-indicator-empty">— No file</span>
                      )}
                    </td>
                    <td>
                      <div className="action-btns">
                        <button
                          className="icon-btn"
                          onClick={() => onEdit(cert)}
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          className="icon-btn"
                          onClick={() => handleDelete(cert)}
                          title="Delete"
                        >
                          🗑️
                        </button>
                        {cert.file_url && (
                          <button
                            className="icon-btn"
                            onClick={() => onDownload(cert.id)}
                            title="Download"
                          >
                            ⬇️
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default CertificateList;
